import type { CSS } from '@stitches/react';
import type { ForwardedRef } from 'react';
import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { ToggleInputProps } from '../../components/ToggleInput/ToggleInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { ToggleInput } from '../../components/ToggleInput/ToggleInput';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { styled } from '../../stitches.config';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { VStack } from '../../utilities/Stack/VStack';
import {
  type AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { PrimaryFieldDescription, PrimaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';

const PrimaryToggleFieldParent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  width: '100%',
});
PrimaryToggleFieldParent.displayName = 'PrimaryToggleFieldParent';

const PrimaryToggleFieldLabelling = styled(AlignStack, {
  position: 'relative',
  gap: '$6',
});
PrimaryToggleFieldLabelling.displayName = 'PrimaryToggleFieldLabelling';

const PrimaryToggleFieldStart = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
  width: '100%',
});
PrimaryToggleFieldStart.displayName = 'PrimaryToggleFieldStart';

const PrimaryToggleFieldEnd = styled('div', {
  maxHeight: '$20',
});
PrimaryToggleFieldEnd.displayName = 'PrimaryToggleFieldEnd';

const PrimaryToggleFieldAlert = styled(Alert, {
  padding: '$8 $12',
});
PrimaryToggleFieldAlert.displayName = 'PrimaryToggleFieldAlert';

const PrimaryToggleFieldContainer = styled('div', {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '$12 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
PrimaryToggleFieldContainer.displayName = 'PrimaryToggleFieldContainer';

export interface PrimaryToggleFieldProps extends BaseFieldProps, ToggleInputProps {
  /**
   * If true, the toggle will be set to an indeterminate state if the `selected` prop is undefined.
   */
  supportsIndeterminate?: boolean;
  containerProps?: { css: CSS };
  children?: React.ReactNode;
}

export function PrimaryToggleFieldInner(
  props: PrimaryToggleFieldProps,
  forwardedRef: ForwardedRef<HTMLLabelElement>,
) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();

  const {
    label = customFieldProps.label,
    errorMessage = customFieldProps.errorMessage,
    description,
    supportsIndeterminate = false,
    accessLevel: accessLevelProp,
    internal,
    containerProps,
    children,
    tooltip,
    ...extraInputProps
  } = props;

  const {
    labelProps,
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
  } = useField({
    label,
    description,
    errorMessage,
  });

  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  const indeterminate =
    supportsIndeterminate &&
    'selected' in customInputProps &&
    customInputProps.selected === undefined;

  const input = (
    <MaybeFieldContainer>
      <PrimaryToggleFieldContainer {...containerProps}>
        <VStack spacing={8}>
          <PrimaryToggleFieldParent>
            <PrimaryToggleFieldStart>
              <PrimaryToggleFieldLabelling
                gap={6}
                preset="body"
                width="fit-content"
                end={
                  accessLevel
                    ? isAccessLevelVisible(accessLevel) && (
                        <FeatureBadge type={accessLevel} size="x-small" />
                      )
                    : internal && <FeatureBadge type="internal" size="x-small" />
                }
              >
                <PrimaryFieldLabel {...labelProps}>{label}</PrimaryFieldLabel>
              </PrimaryToggleFieldLabelling>
              {description && (
                <PrimaryFieldDescription {...descriptionProps}>
                  {description}
                </PrimaryFieldDescription>
              )}
            </PrimaryToggleFieldStart>
            <PrimaryToggleFieldEnd>
              <ToggleInput
                aria-label={`${label}`}
                size="large"
                indeterminate={indeterminate}
                {...mergeProps(customInputProps, inputProps, extraInputProps, {
                  ref: forwardedRef,
                })}
              />
            </PrimaryToggleFieldEnd>
          </PrimaryToggleFieldParent>
          {children}
        </VStack>
        <FieldDivider context="primary" />
      </PrimaryToggleFieldContainer>
      {errorMessage && (
        <FieldErrorMessages>
          <PrimaryToggleFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );

  return tooltip ? (
    <Tooltip contents={tooltip} asChild={false}>
      {input}
    </Tooltip>
  ) : (
    input
  );
}

export const PrimaryToggleField = React.forwardRef(PrimaryToggleFieldInner);
